import React, { useEffect, useState } from 'react';
import { authFetch } from '../utils/apiHelper';
import { API_URL } from '../config';
import { modalPrimaryBtn, modalSecondaryBtn, modalDangerBtn, MODAL_BTN_ROW, modalCard, modalTitle, modalBody } from '../modalButtons';
import ModalOverlay from './ModalOverlay';

const rowBtnStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 4,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const thStyle = {
  textAlign: 'left',
  fontSize: 10,
  fontWeight: 600,
  color: '#999',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  padding: '0 10px 8px 0',
  borderBottom: '1px solid #eee',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  fontSize: 12,
  color: '#1a1a1a',
  fontFamily: 'Inter, Arial, sans-serif',
  padding: '10px 10px 10px 0',
  borderBottom: '1px solid #f0f0f0',
  verticalAlign: 'middle',
};

// Listens for the 'Shares' item in the top-nav settings dropdown, shown on
// every page. Not tied to a specific modelId — lists every active share
// across models the caller administers.
const GlobalShareModal = () => {
  const [open, setOpen] = useState(false);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const loadShares = () => {
    setLoading(true);
    authFetch(`${API_URL}?listShares=true`)
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => setShares(data.shares || []))
      .catch(() => setShares([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const handle = (e) => {
      if (e.detail !== 'share') return;
      setOpen(true);
      loadShares();
    };
    window.addEventListener('dashboardNav', handle);
    return () => window.removeEventListener('dashboardNav', handle);
  }, []);

  useEffect(() => {
    if (open) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [open]);

  const handleCopy = async (shareId) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/s/${shareId}`);
      setCopiedId(shareId);
      setTimeout(() => setCopiedId(null), 1800);
    } catch {}
  };

  const confirmDelete = async () => {
    const shareId = confirmId;
    setConfirmId(null);
    setDeletingId(shareId);
    try {
      const res = await authFetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deleteShare: true, shareId }),
      });
      if (!res.ok) throw new Error();
      setShares(prev => prev.filter(s => s.shareId !== shareId));
    } catch {
    } finally {
      setDeletingId(null);
    }
  };

  if (!open) return null;

  return (
    <ModalOverlay>
      <style>{`
        @media (max-width: 520px) {
          .share-table td, .share-table th { padding-right: 6px !important; font-size: 11px !important; }
        }
      `}</style>
      <div
        className="modal-content"
        style={{
          backgroundColor: '#fff',
          borderRadius: 12,
          padding: '28px 20px 24px',
          // +26 over the old 540 — exactly the gutter reserved below for the
          // list's scrollbar, so the extra width goes entirely to that
          // gutter rather than stretching the rest of the modal.
          maxWidth: 566,
          width: '90%',
          pointerEvents: 'auto',
          textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <span style={{ fontSize: 16, fontWeight: 600, fontFamily: 'Inter, Arial, sans-serif', color: '#1a1a1a' }}>
            Shares
          </span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#999', lineHeight: 1 }}>✕</button>
        </div>

        {loading && (
          <p style={{ fontSize: 13, color: '#888', margin: '0 0 22px', fontFamily: 'Inter, Arial, sans-serif' }}>Loading…</p>
        )}

        {!loading && shares.length === 0 && (
          <p style={{ fontSize: 13, color: '#888', margin: '0 0 22px', fontFamily: 'Inter, Arial, sans-serif' }}>
            Your shared links will appear here.
          </p>
        )}

        {!loading && shares.length > 0 && (
          // paddingRight reserves a gutter for the scrollbar (rendered here
          // regardless of platform via scrollbarGutter, plus the explicit
          // padding as a fallback/extra buffer for browsers that ignore it)
          // so it sits clear of the row content instead of overlapping the
          // copy/delete buttons in the last column — those used to butt
          // right up against the scroll container's own edge, with nothing
          // between them and the scrollbar track.
          <div style={{ maxHeight: 340, overflowY: 'auto', overflowX: 'auto', scrollbarGutter: 'stable', paddingRight: 14, margin: '0 0 20px' }}>
            <table className="share-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>ID</th>
                  <th style={thStyle}>Created</th>
                  <th style={thStyle}>Expires</th>
                  <th style={{ ...thStyle, textAlign: 'right', padding: '0 0 8px' }}></th>
                </tr>
              </thead>
              <tbody>
                {shares.map((share) => {
                  // Same-day check is on local calendar date, not a 24h
                  // window — a share expiring at 1am today is "today" just
                  // as much as one expiring at 11pm today. isExpired takes
                  // priority: a share already past its expiry (even if
                  // that moment was earlier today) is red, not yellow.
                  const isExpired = share.expiresAt <= Date.now();
                  const isExpiringToday = !isExpired && new Date(share.expiresAt).toDateString() === new Date().toDateString();
                  const expiresColor = isExpired ? '#ef4444' : isExpiringToday ? '#f59e0b' : '#888';
                  return (
                  <tr key={share.shareId}>
                    <td style={{ ...tdStyle, fontWeight: 500, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {share.modelName || '—'}
                    </td>
                    <td style={{ ...tdStyle, color: '#888', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                      …{share.shareId.slice(-5)}
                    </td>
                    <td style={{ ...tdStyle, color: '#888', whiteSpace: 'nowrap' }}>
                      {new Date(share.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </td>
                    <td style={{ ...tdStyle, color: expiresColor, whiteSpace: 'nowrap' }}>
                      {new Date(share.expiresAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </td>
                    <td style={{ ...tdStyle, padding: '10px 0', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button type="button" onClick={() => handleCopy(share.shareId)} title="Copy link" style={rowBtnStyle}>
                        {copiedId === share.shareId ? (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        ) : (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmId(share.shareId)}
                        title="Delete link"
                        disabled={deletingId === share.shareId}
                        style={{ ...rowBtnStyle, opacity: deletingId === share.shareId ? 0.5 : 1 }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <button type="button" onClick={() => setOpen(false)} style={{ ...modalPrimaryBtn, width: '100%' }}>
          Close
        </button>
      </div>

      {confirmId && (
        <ModalOverlay onClick={(e) => e.stopPropagation()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={modalCard}>
            <p style={modalTitle}>Delete this share link?</p>
            <p style={modalBody}>Anyone using it will lose access immediately. This can&rsquo;t be undone.</p>
            <div style={MODAL_BTN_ROW}>
              <button type="button" onClick={() => setConfirmId(null)} style={{ ...modalSecondaryBtn, flex: 1 }}>
                Cancel
              </button>
              <button type="button" onClick={confirmDelete} style={{ ...modalDangerBtn, flex: 1 }}>
                Delete
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </ModalOverlay>
  );
};

export default GlobalShareModal;
